import { useEffect, useMemo, useRef, useState } from 'react';

import { useLocation } from 'react-router-dom';

import {
  apiErrorMessage,
  createOrder,
  fetchPayoutDefault,
  fetchStorefrontConfig,
  isPricingUnavailable,
  type CreatedOrder,
  type StorefrontConfig,
  type Tier,
} from '../lib/api';
import { useOdds } from '../hooks/useOdds';
import { useWinningValue } from '../hooks/useWinningValue';
import { useQuote } from '../hooks/useQuote';
import SegmentedControl from '../components/SegmentedControl';
import HashratePicker from '../components/HashratePicker';
import PayoutAddressField, { type PayoutState } from '../components/PayoutAddressField';

import OddsDisplay from '../components/OddsDisplay';
import OrderSummary from '../components/OrderSummary';
import DiscountCodeField from '../components/DiscountCodeField';
import OrderReviewSummary from '../components/OrderReviewSummary';
import SiteHeader from '../components/SiteHeader';
import SiteFooter from '../components/SiteFooter';
import PageShell from '../layout/PageShell';
import { useSession } from '../hooks/useSession';
import { formatSavingsBasisPoints, formatTermDays } from '../lib/format';
import { startCheckout } from '../lib/saturnshift';
import {
  MINING_MODE_DESCRIPTIONS,
  MINING_MODE_LABELS,
  POWERPOOL_TEAM_URL,
  TEAM_SOLO_MODE,
  teamSoloDescriptionParts,
} from '../lib/miningModes';
import { emailProblem } from '../lib/email';

/**
 * The Configure page.
 *
 * Reading order, which the layout is built to deliver:
 *   choose mining power -> understand your odds -> configure the purchase.
 *
 * Orchestration only: it holds the customer's selections and passes server-supplied
 * values to presentation components. It decides no prices, computes no odds, and
 * validates no addresses -- each of those has an authoritative owner on the server.
 *
 * The rule that shapes the whole page is unchanged, and it is what let custom hashrate
 * become purchasable without loosening anything: A CONFIGURATION WITHOUT A SERVER PRICE
 * CANNOT PROCEED. What changed is only which configurations the server can price. The
 * page still refuses to submit anything it has not been quoted, and still never works
 * out a price of its own -- the button is gated on the presence of a server quote, not
 * on membership of a tier.
 */

/*
 * Company proof points, supplied by Sidney as the owner of Metered Mining and approved
 * by Bertrand for publication.
 *
 * THESE ARE CLAIMS ABOUT THE BUSINESS, NOT COMPUTED VALUES. Nothing in this application
 * measures a fleet size or a customer count, so none of these figures may ever be
 * presented as live or derived -- they are static statements of record, and they change
 * only when the product owner supplies new ones.
 *
 * They answer a different question from the hero's trust points ("why should I trust
 * this company?" rather than "what am I buying, and why is it real?"), which is why they
 * live in their own section rather than being mixed into one row of badges.
 */
const CREDIBILITY_POINTS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '1,000+', label: 'Miners Managed' },
  { value: '250+', label: 'Customers Worldwide' },
  { value: 'Since 2023', label: 'Operating' },
  { value: 'Wisconsin', label: 'Facilities In' },
];

/*
 * What the customer is buying and why it is real. Three short markers, not a feature
 * list: each one is a statement the product can stand behind.
 */
const TRUST_POINTS: ReadonlyArray<string> = [
  'Real mining hardware',
  'Independently verifiable hashrate',
  'Block rewards paid directly to your wallet',
];

/**
 * The configuration fields the page cannot render without.
 *
 * Only the ones with no safe default: the slider's bounds and step come from the server
 * and nothing here may invent them, because inventing a range would let a customer
 * select an amount the server would refuse to price.
 */
function missingConfigFields(config: StorefrontConfig | null): string[] {
  const custom = config?.customHashrate;
  const required: Array<[string, unknown]> = [
    ['tiers', config?.tiers?.length ? config.tiers : undefined],
    ['termDaysOptions', config?.termDaysOptions?.length ? config.termDaysOptions : undefined],
    ['customHashrate.minTh', custom?.minTh],
    ['customHashrate.maxTh', custom?.maxTh],
    ['customHashrate.incrementTh', custom?.incrementTh],
  ];

  return required
    .filter(([, value]) => typeof value === 'undefined' || value === null)
    .map(([name]) => name);
}

/** A numbered section heading, so the journey reads as steps rather than a long form. */
function Step({
  number,
  title,
  note,
  children,
}: {
  number: number;
  title: string;
  note?: string;
  children: React.ReactNode;
}) {
  const headingId = `step-${number}-heading`;

  return (
    <section aria-labelledby={headingId}>
      <div className="mb-4 flex items-baseline gap-3">
        <span
          className="mm-numeric flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-semibold"
          style={{
            background: 'var(--mm-accent-soft)',
            color: 'var(--mm-accent)',
            border: '1px solid var(--mm-accent-border)',
          }}
          aria-hidden="true"
        >
          {number}
        </span>
        <div>
          <h2 id={headingId} className="mm-section-title">
            {title}
          </h2>
          {note && <p className="mm-section-note">{note}</p>}
        </div>
      </div>
      {children}
    </section>
  );
}

export default function Configure() {
  // Fetched once per page load: it depends on nothing the customer configures.
  const winningValue = useWinningValue();

  /*
   * THE SAVING EACH DURATION OFFERS AT THE CURRENTLY SELECTED HASHRATE.
   *
   * Published by the quote, because a custom amount has no tier in the catalogue -- only
   * the server knows which band prices 610 TH/s, and therefore what each term saves at
   * that amount.
   *
   * THE LAST KNOWN MAP IS HELD WHILE A NEW QUOTE IS IN FLIGHT. Without it the hints
   * would blank on every hashrate change and the duration row would flash. The values
   * are briefly the previous hashrate's, for the few hundred milliseconds a quote takes,
   * and they are PRESENTATION ONLY -- no price, total or order field is derived from
   * them, so a momentarily stale percentage cannot reach anything that matters. The
   * price itself is never shown stale: OrderSummary clears it while re-quoting.
   */
  const lastDurationSavings = useRef<Record<string, number> | null>(null);

  const [config, setConfig] = useState<StorefrontConfig | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);

  const [selectedTierId, setSelectedTierId] = useState<string | null>(null);
  const [hashrateTh, setHashrateTh] = useState<number>(0);
  const [termDays, setTermDays] = useState<number>(30);
  const [miningMode, setMiningMode] = useState<string>('solo');

  // The code currently applied to the quote. Null until a customer applies one; the
  // server decides whether it is valid and what it is worth.
  const [discountCode, setDiscountCode] = useState<string | null>(null);

  const [payoutAddress, setPayoutAddress] = useState('');
  const [payoutState, setPayoutState] = useState<PayoutState>({ status: 'empty' });

  /*
   * HAS THE CUSTOMER TOUCHED THIS FIELD YET?
   *
   * A ref rather than state, because nothing renders differently for it -- and because
   * the account-default response has to read the CURRENT answer when it lands, not the
   * one captured when the request was made. See the prefill effect below.
   */
  const payoutTouched = useRef(false);

  /** Every customer edit, and only a customer edit, marks the field as theirs. */
  function handlePayoutChange(next: string) {
    payoutTouched.current = true;
    setPayoutAddress(next);
  }
  const [email, setEmail] = useState('');
  /*
   * Whether the customer has finished with the email field at least once.
   *
   * VALIDATION APPEARS ON BLUR, NOT ON EVERY KEYSTROKE. Marking a field invalid while
   * someone is still typing the second character is noise -- "y" is not a mistake, it is
   * an unfinished address. The submit gate does not wait for this, so the button stays
   * correct whether or not the message is showing yet.
   */
  const [emailTouched, setEmailTouched] = useState(false);
  // A signed-in customer's order belongs to their account -- the backend enforces it.
  const { state: session } = useSession();
  const signedInEmail = session.status === 'authenticated' ? session.customer.email : null;

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [created, setCreated] = useState<CreatedOrder | null>(null);

  /*
   * THE SATURNSHIFT HANDOFF, WHICH IS SEPARATE FROM THE ORDER.
   *
   * Deliberately its own state, and deliberately NOT allowed to clear `created`. A
   * failed handoff means "we could not open the payment page", never "the order did not
   * happen" -- the order is persisted, and a capped discount may already be holding a
   * reservation for it. Retrying re-runs this handoff for the SAME order; it must never
   * fall back to POST /api/orders.
   */
  const [handoffState, setHandoffState] = useState<
    { status: 'idle' } | { status: 'starting' } | { status: 'failed' }
  >({ status: 'idle' });

  /*
   * NAVIGATING HOME LEAVES THE POST-ORDER SCREENS.
   *
   * The wordmark and the "Configure" nav item both link to `/`, which is where this page
   * already lives. React Router does not unmount a component for a navigation to the
   * route it is already on, so without this the post-order screens survived the click and
   * the link appeared dead -- the one place in the application where a header link did
   * nothing.
   *
   * `location.key` changes on every navigation, including one to the current path, which
   * is what distinguishes a deliberate click from a re-render. On first mount there is
   * nothing to clear and this is a no-op.
   *
   * The configuration itself is deliberately left alone: someone who came back to the
   * configurator has not asked to have their choices thrown away.
   */
  /*
   * =========================================================================
   * PREFILL THE PAYOUT FIELD FROM THE SAVED ACCOUNT WALLET.
   *
   * A signed-in customer who has set a default wallet should not have to paste it again
   * on every order. The ACCOUNT DEFAULT is the only source: not their last order, not a
   * provisioning record, not the purchase snapshot. An address used once for one rental
   * is not a statement about where the next one should pay, and quietly reusing it would
   * be us deciding that on their behalf.
   *
   * ANONYMOUS CUSTOMERS ASK NOTHING. The endpoint is authenticated, and calling it
   * without a session would be a guaranteed 401 on every visit to the storefront.
   *
   * IT IS A CONVENIENCE, NOT A DEPENDENCY. A failure is swallowed: the field stays empty,
   * the configurator is completely usable, and nothing about ordering is blocked. There
   * is deliberately no spinner and no message -- there is nothing the customer needs to
   * do about it, and a notice would imply otherwise.
   *
   * PREFILLED IS NOT PRE-APPROVED. The value goes into the same field, through the same
   * shared server-backed check, and `canSubmit` still requires a `valid` verdict. A
   * wallet that was fine when it was saved must still be confirmed now.
   */
  useEffect(() => {
    if (session.status !== 'authenticated') return undefined;
    // Already theirs. Nothing to prefill into.
    if (payoutTouched.current) return undefined;

    const controller = new AbortController();

    fetchPayoutDefault(controller.signal)
      .then((saved) => {
        if (controller.signal.aborted) return;
        /*
         * RE-CHECKED HERE, AFTER THE AWAIT. This is the race that matters: a customer can
         * start typing while the request is in flight, and overwriting what they typed
         * with a stale saved value would be the worst possible field to do it in.
         */
        if (payoutTouched.current) return;
        if (!saved.payoutAddress) return;

        setPayoutAddress(saved.payoutAddress);
      })
      .catch(() => {
        // Convenience only. An empty field is the correct fallback.
      });

    return () => controller.abort();
  }, [session.status]);

  const location = useLocation();

  useEffect(() => {
    setCreated(null);
  }, [location.key]);

  /** Guards against a double click creating two orders while the first is in flight. */
  const submissionInFlight = useRef(false);

  // --- Configuration load ----------------------------------------------------

  useEffect(() => {
    const controller = new AbortController();

    fetchStorefrontConfig(controller.signal)
      .then((loaded) => {
        /*
         * TRUST, THEN VERIFY.
         *
         * The picker's bounds are the server's, by design -- it holds no fallback range
         * of its own. That is right, and it means a response missing them cannot be
         * rendered at all. A frontend that cannot trust the server's bounds must not
         * pretend to: it says so plainly instead of drawing a half-configured control.
         *
         * In practice this fires on VERSION SKEW -- a page newer than the API it is
         * talking to. The console line names the culprit, because "Something went wrong"
         * is useless to whoever has to fix it.
         */
        const missing = missingConfigFields(loaded);
        if (missing.length > 0) {
          console.error(
            `Storefront config is missing: ${missing.join(', ')}. ` +
              'This usually means the API is running an older build than this page.',
            loaded?.customHashrate,
          );
          setConfigError('The store is temporarily unavailable. Please try again shortly.');
          return;
        }

        setConfig(loaded);
        setConfigError(null);

        /*
         * THE BASELINE CONFIGURATION, AT the product owner's request: the smallest
         * rental this storefront sells, so the first price a customer sees is the
         * entry point rather than a mid-range default.
         *
         * STILL CHOSEN FROM THE SERVER'S CATALOGUE, not hardcoded. 10 TH/s and 7 days
         * are looked up among the options the server published; if either ever stops
         * being offered, the fallbacks pick the smallest tier and the shortest term
         * rather than selecting something that cannot be priced.
         */
        const initial =
          loaded.tiers.find((tier) => tier.hashrateTh === 10) ??
          [...loaded.tiers].sort((a, b) => a.hashrateTh - b.hashrateTh)[0] ??
          null;
        if (initial) {
          setSelectedTierId(initial.id);
          setHashrateTh(initial.hashrateTh);
        }

        const shortestTerm = [...loaded.termDaysOptions].sort((a, b) => a - b)[0];
        if (loaded.termDaysOptions.includes(7)) setTermDays(7);
        else if (shortestTerm) setTermDays(shortestTerm);
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        setConfigError(apiErrorMessage(error, 'Could not load the store right now.'));
      });

    return () => controller.abort();
  }, []);

  const selectedTier: Tier | null = useMemo(
    () => config?.tiers.find((tier) => tier.id === selectedTierId) ?? null,
    [config, selectedTierId],
  );

  const { state: oddsState, retry: retryOdds } = useOdds({
    hashrateTh: hashrateTh > 0 ? hashrateTh : null,
    termDays,
  });

  // The authoritative price for whatever is currently selected, tier or not.
  const { state: quoteState } = useQuote({
    hashrateTh: hashrateTh > 0 ? hashrateTh : null,
    termDays,
    discountCode,
  });

  if (quoteState.status === 'ready') {
    lastDurationSavings.current = quoteState.quote.combinedSavingsBasisPointsByTermDays;
  }

  const durationSavings =
    quoteState.status === 'ready'
      ? quoteState.quote.combinedSavingsBasisPointsByTermDays
      : lastDurationSavings.current;

  // --- Selection handlers ----------------------------------------------------

  function moveSlider(value: number) {
    setHashrateTh(value);
    // Landing exactly on a preset highlights it; anything else is a custom amount. This
    // now affects LABELLING ONLY -- it no longer decides whether the order can proceed,
    // because both are priced by the same server call.
    const match = config?.tiers.find((tier) => tier.hashrateTh === value) ?? null;
    setSelectedTierId(match?.id ?? null);
    setSubmitError(null);
  }

  // --- Order handoff ---------------------------------------------------------

  // A server quote is the licence to submit. Not a tier, not a locally computed price:
  // if the server has not said what this costs, the customer cannot buy it.
  const quoted = quoteState.status === 'ready' ? quoteState.quote : null;

  /*
   * THE EMAIL THAT WILL ACTUALLY BE USED. A signed-in customer's order belongs to their
   * account, so the session's address is what gates submission and what is sent -- one
   * value, so the button and the payload cannot disagree. The backend decides regardless.
   */
  const orderEmail = signedInEmail ?? email.trim();

  /*
   * THE TYPED ADDRESS ONLY. A signed-in customer's address comes from their session, is
   * already the account's, and the field is read-only -- so it is never re-judged here.
   * Validating it would let a stale or unusual-but-real address on an existing account
   * block a purchase the customer cannot fix from this page.
   */
  const emailError = signedInEmail ? null : emailProblem(email);

  // Shown once the field has been left, so a half-typed address is not called wrong.
  const showEmailError = emailTouched && emailError !== null;

  // Computed once per render rather than inside the options map. Null if the phrase is
  // no longer in the copy, in which case the plain description renders.
  const teamParts = teamSoloDescriptionParts();

  const canSubmit =
    quoted !== null && payoutState.status === 'valid' && emailError === null && !submitting;

  const blockedReason = !quoted
    ? quoteState.status === 'loading'
      ? 'Getting a price…'
      : quoteState.status === 'unpriceable' || quoteState.status === 'error'
        ? quoteState.reason
        : 'Choose an amount to continue.'
    : payoutState.status !== 'valid'
      ? 'Add a valid Bitcoin payout address to continue.'
      : emailError
        ? // The field's own message is the specific one; this is the summary beside the
          // button, and it must not contradict it.
          email.trim().length === 0
          ? 'Add your email to continue.'
          : 'Enter a valid email address to continue.'
        : null;

  async function submit(event: React.FormEvent) {
    event.preventDefault();

    // Belt and braces against a double submit: state for the render, a ref for the race
    // between two clicks in the same tick. Neither is a real guarantee -- the server is
    // authoritative -- but they stop the obvious duplicate.
    if (submissionInFlight.current || !canSubmit || !quoted) return;
    submissionInFlight.current = true;
    setSubmitting(true);
    setSubmitError(null);

    try {
      // Only the five fields the server accepts. No price, total, or tier id -- and in
      // particular NOT the total displayed a moment ago. The server re-quotes from the
      // authoritative price list and its figure is the one that binds; if the two ever
      // disagree, the customer is charged the server's.
      const result = await createOrder({
        hashrateTh,
        termDays,
        miningMode,
        payoutAddress: payoutAddress.trim(),
        email: orderEmail,
        // Sent only when the server itself confirmed it applies. The server resolves and
        // revalidates it regardless -- this is not proof of anything.
        ...(quoted.discount?.applied ? { discountCode: quoted.discount.code } : {}),
      });

      setCreated(result);
      // The order exists now, so the review screen takes over from the configurator.
    } catch (error: unknown) {
      setSubmitError(
        isPricingUnavailable(error)
          ? apiErrorMessage(error, 'That amount is not on sale.')
          : apiErrorMessage(error, 'We could not create that order.'),
      );
    } finally {
      submissionInFlight.current = false;
      setSubmitting(false);
    }
  }

  /**
   * Hand off to SaturnShift for the order that already exists.
   *
   * READS ONLY `created`. Nothing from the configurator reaches the widget: after the
   * order was written, the form is history and the server's response is the fact.
   *
   * A failure leaves the order untouched and offers a retry. Because
   * `externalReference` and `idempotencyKey` are both the order number, a retry asks
   * SaturnShift for the SAME checkout rather than creating a second one.
   */
  async function beginCheckout() {
    if (!created || handoffState.status === 'starting') return;

    setHandoffState({ status: 'starting' });

    try {
      await startCheckout(created);
      // On success the widget navigates away; nothing after this runs in practice. The
      // state is left as `starting` deliberately -- there is no success to report here,
      // because success is a payment, and only the server can observe that.
    } catch {
      setHandoffState({ status: 'failed' });
    }
  }

  // --- Render ----------------------------------------------------------------

  if (configError) {
    return (
      <PageShell width="mx-auto w-full max-w-2xl px-6 py-24">
          <h1 className="mm-section-title">Something went wrong</h1>
          <p className="mm-note mm-note--danger mt-4" role="alert">
            <span>{configError}</span>
          </p>
      </PageShell>
    );
  }

  if (!config) {
    return (
      <PageShell width="mx-auto w-full max-w-2xl px-6 py-24">
        <p className="mm-help" role="status">
          Loading…
        </p>
      </PageShell>
    );
  }

  /*
   * ===============================================================================
   * THE HANDOFF TO SATURNSHIFT'S HOSTED CHECKOUT
   * ===============================================================================
   *
   * This replaced a placeholder screen that stood here while payments were unintegrated.
   * The order already exists and is persisted; what remains is to send the customer to
   * SaturnShift to pay for it.
   *
   * THE ORDER IS AUTHORITATIVE AND ALREADY WRITTEN. Everything below reads
   * `created.order` -- the server's response -- and nothing reads the form above it.
   * After `POST /api/orders` succeeds, the configurator's state is history; the order is
   * the fact.
   *
   * NOTHING HERE MAKES AN ORDER PAID. The widget navigates away and hands nothing back:
   * no transaction id, no callback, no success signal. A customer reaching the return URL
   * proves only that a browser followed a redirect. Payment state moves on a verified
   * webhook or an authenticated reconciliation, and on nothing else.
   *
   * RETRY MUST NOT CREATE A SECOND ORDER. `handoffState` governs this screen alone;
   * `created` is never cleared by a failure, so the retry button re-runs the handoff for
   * the SAME order -- same number, same amount, same idempotency key. Calling
   * `POST /api/orders` again would mint a second order and, for a capped discount, take a
   * second reservation for a customer who is buying one thing.
   */
  if (created) {
    const failed = handoffState.status === 'failed';

    return (
      /*
        PADDING THAT SIZES TO THE CONTENT.

        This screen carried py-20 sm:py-28 -- 5rem top AND bottom, 7rem on desktop -- on
        top of a shell that is already `min-h-dvh` with a `flex-1` main. The flex rule
        alone puts the footer at the bottom of a short page; the extra 10-14rem was doing
        nothing but pushing a short summary past the fold and leaving a band of empty
        space above the footer. Sidney reported both symptoms, and they were one cause.

        That fixed most of it. Manual testing then showed the desktop page still cleared
        the viewport by a little, so the remaining slack is trimmed HERE and only here --
        `mm-review-shell` scopes the rest of the rhythm (see styles/index.css) so no
        other page, and no shared token, moves with it.

        MOBILE IS UNTOUCHED: py-10 is the same 2.5rem it already had, and every scoped
        rule is inside a min-width query. A viewport too short for the content still
        scrolls normally -- nothing here caps a height or hides an overflow.
      */
      <PageShell width="mm-review-shell mx-auto w-full max-w-2xl px-6 py-10">
          <p className="mm-eyebrow">Order created</p>
          <h1 className="mm-display mt-3">Review your order</h1>
          <p className="mm-lede mt-4">
            Everything looks good. Continue to secure checkout to complete payment.
          </p>

          {/*
            THE FROZEN PURCHASE, NOT A FRESH QUOTE.

            Every figure is read from `created.order` -- the order the server just
            persisted, including its immutable purchase snapshot. This screen re-quotes
            nothing and recomputes nothing, so what it shows is what was bought even if the
            catalogue changes a minute from now. See components/OrderReviewSummary.

            The total shown here is the same figure the widget receives; there is only one,
            and the server produced it.
          */}
          {/* The email travels as a prop: the order DTO deliberately does not carry it. */}
          <OrderReviewSummary order={created.order} email={orderEmail} />

          {failed && (
            <p
              role="alert"
              className="mm-note mm-note--danger mt-6"
              data-testid="checkout-error"
            >
              <span>
                We couldn&apos;t open the payment page just now. Your order is saved, so
                you can try again.
              </span>
            </p>
          )}

          {/*
            THE EMAIL SAFEGUARD MOVED INTO THE ORDER CARD.

            It was a panel of its own here, directly above the button. That put it in the
            right place to be read but cost about 8rem, which re-created the desktop
            scrollbar the spacing pass had just removed. It now continues the order
            metadata inside OrderReviewSummary, beneath the order number, where it reuses
            that block's border and padding and costs roughly a third as much.

            The recognition is unchanged and so is the reasoning: there is no edit action,
            because by this point the email IS the customer the persisted order belongs to.
            See the note in components/OrderReviewSummary.
          */}

          <div className="mt-6 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => void beginCheckout()}
              disabled={handoffState.status === 'starting'}
              data-testid="start-checkout"
              className="mm-btn-primary inline-flex items-center justify-center sm:w-auto"
            >
              {handoffState.status === 'starting'
                ? 'Opening secure checkout…'
                : failed
                  ? 'Try again'
                  : 'Continue to secure checkout'}
            </button>
          </div>

          {/*
            ONE ACTION, DELIBERATELY.

            This screen IS the detailed order -- power, term, mode, the full pricing
            ladder, payout, odds and the order number are all above. A second button to
            "view order details" offered a page with strictly less on it, which made the
            customer choose between two things when only one of them moves them forward.

            Nothing is lost by removing it: the order number is in the summary, the order
            is saved, and /orders/<number> remains reachable directly and from the receipt
            email.
          */}
          {/* Only when it is false: the receipt is best effort, a mail-provider outage
              never fails an order, and the address itself is already in the card above. */}
          {!created.receipt.sent && (
            <p className="mm-help mt-5" data-testid="receipt-not-sent">
              We couldn&rsquo;t send your confirmation email just now. Your order is saved.
            </p>
          )}

          <p className="mm-help mt-3">
            No payment has been taken yet. You&rsquo;ll complete payment securely with our
            payment provider.
          </p>
      </PageShell>
    );
  }

  return (
    <div className="flex min-h-dvh flex-col">
      <SiteHeader />

      {/* Asymmetric bottom padding on a phone: the footer supplies its own top margin
          and its own surface, so the two stacked into a 96px band of page background
          between the last card and the footer. Desktop spacing is unchanged. */}
      <main className="mx-auto w-full max-w-[1240px] px-5 pb-6 pt-10 sm:px-8 sm:pb-14 sm:pt-14 lg:px-10 flex-1 min-w-0">
        {/*
          --- Introduction -------------------------------------------------

          THE HERO ANSWERS ONE QUESTION: what am I buying, and why is it real?

          The custody sentence that used to sit here has been removed as a duplicate. It
          said the same thing as the third trust marker, twice, in the same eyeful. The
          full disclosure is NOT lost: it is stated in Step 4, immediately beside the
          field where the customer types the address it concerns, and again on the order
          status page. That is the more useful place for it -- a custody fact means most
          at the moment of entering the wallet it applies to.
        */}
        <div className="mb-8 lg:mb-10">
          <h1 className="mm-display max-w-3xl">Rent Real Mining Shares</h1>
          <p className="mm-lede mt-4">
            Select your hashrate, term, and mining method. Every rental is backed by physical
            equipment operated at our facilities in Wisconsin.
          </p>

          <ul className="mm-trust-list mt-5" data-testid="trust-points">
            {TRUST_POINTS.map((point) => (
              <li key={point} className="mm-trust-marker">
                {/* Decorative: the text alone carries the meaning, so the mark is hidden
                    from assistive technology rather than read out three times. */}
                <span className="mm-trust-check" aria-hidden="true">
                  <svg viewBox="0 0 12 12" width="10" height="10" focusable="false">
                    <path
                      d="M1.5 6.2 4.4 9 10.5 2.6"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                {point}
              </li>
            ))}
          </ul>
        </div>

        {/*
          --- Company proof ------------------------------------------------

          A DIFFERENT QUESTION FROM THE HERO: why should I trust this company?

          Kept to one compact band so it cannot become an About page standing between
          the customer and the configurator. Four static figures, then the one thing a
          sceptic can independently check.

          THE BLOCK IS STATED, NOT SOLD. Sidney supplied it as his previously mined
          block, so it is described as exactly that. There is no claim, implied or
          otherwise, that it predicts a future result -- the odds panel is the only place
          this product talks about likelihood, and it does so with real numbers.
        */}
        {/*
          THE HEADING IS GONE, at Sidney's request (round 2): it restated the hero's
          three trust markers directly beneath them, in the same eyeful.

          THE SECTION KEEPS AN ACCESSIBLE NAME. It was named by that <h2> through
          aria-labelledby; removing the element without replacing the name would leave a
          landmark a screen-reader user hears as unlabelled, which is a worse outcome
          than the duplication. An aria-label carries it now -- not visible, so it cannot
          reintroduce the repetition on screen.
        */}
        <section className="mm-credibility mb-9 lg:mb-12" aria-label="About Metered Mining" data-testid="credibility">
          <dl className="mm-stat-grid">
            {CREDIBILITY_POINTS.map((point) => (
              // Semantically the label is the term and the figure defines it; visually
              // the figure leads. CSS reverses the pair rather than the markup lying.
              <div key={point.label} className="mm-stat">
                <dt className="mm-stat-label">{point.label}</dt>
                <dd className="mm-stat-value mm-numeric">{point.value}</dd>
              </div>
            ))}
          </dl>

          <div className="mm-credibility-proof">
            <p className="mm-numeric text-sm" style={{ color: 'var(--mm-text-muted)' }}>
              Previously mined: <span className="font-semibold">Block #947128</span>
            </p>
            <a
              href="https://soloblocks.io/block/947128"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="proof-link"
              className="text-sm font-medium"
              style={{ color: 'var(--mm-accent)' }}
            >
              Verify on SoloBlocks ↗
            </a>
          </div>
        </section>

        <form
          onSubmit={submit}
          className="grid gap-12 lg:grid-cols-[minmax(0,1fr)_400px] lg:gap-12 xl:gap-16"
        >
          {/* --- Configuration ------------------------------------------- */}
          <div className="grid" style={{ rowGap: 'var(--mm-section-gap)' }}>
            <Step number={1} title="Choose your mining power">
              <HashratePicker
                config={config}
                valueTh={hashrateTh}
                termDays={termDays}
                selectedTier={selectedTier}
                quoteState={quoteState}
                onChange={moveSlider}
              />
            </Step>

            <Step number={2} title="Choose how long">
              <SegmentedControl
                legend="Duration"
                name="term"
                value={termDays}
                onChange={(value) => {
                  setTermDays(value);
                  setSubmitError(null);
                }}
                /*
                  EACH DURATION STATES ITS OWN SAVING, from the server's figure for the
                  hashrate currently selected. Change the mining power and every one of
                  these moves, because the quote that produced them was re-fetched for
                  the new amount.

                  Nothing is computed here. `formatSavingsBasisPoints` rounds basis
                  points for display; the percentages themselves come from the same
                  pricing domain that produced the price.
                */
                options={config.termDaysOptions.map((days) => ({
                  value: days,
                  label: formatTermDays(days),
                  hint: formatSavingsBasisPoints(durationSavings?.[String(days)]),
                }))}
              />
            </Step>

            <Step number={3} title="Choose how you mine">
              <SegmentedControl
                legend="Mining mode"
                name="mode"
                variant="card"
                value={miningMode}
                onChange={setMiningMode}
                /*
                  THE TEAM LINK IS INSIDE THE SENTENCE, NOT UNDER IT.

                  It has moved twice. It began below the whole control, reading as helper
                  text for the section; then into a footer inside the Team Solo card,
                  which fixed the attribution but still cost a line of its own and the
                  spacing that came with it. Sidney asked for neither -- so the words
                  "Metered Mining team", which the description already contains, are now
                  simply the link.

                  THE COPY IS UNCHANGED. Not paraphrased, not extended: the phrase is
                  located in the canonical description and wrapped, so the sentence a
                  customer reads is the same one product owns. If that phrase ever stops
                  appearing there, teamSoloDescriptionParts returns null and the plain
                  description renders -- a missing link, never mangled copy.

                  STILL OUTSIDE THE <label>. The hint renders beneath the label rather than
                  inside it, so clicking the link does not also select the radio and the
                  label's accessible name stays the option, not the option plus a link.

                  IT SAYS NOTHING ABOUT ODDS, AND MUST NOT. Team Solo odds against the
                  team's aggregate hashrate are parked at Sidney's instruction; the odds
                  panel is computed from the customer's own rented hashrate in both
                  modes. So it is somewhere to look, never a figure or a factor.
                */
                options={config.miningModes.map((mode) => ({
                  value: mode,
                  label: MINING_MODE_LABELS[mode] ?? mode,
                  hint:
                    mode === TEAM_SOLO_MODE && teamParts ? (
                      <>
                        {teamParts.before}
                        <a
                          href={POWERPOOL_TEAM_URL}
                          target="_blank"
                          // noopener stops the opened page reaching back through window.opener.
                          rel="noopener noreferrer"
                          className="underline underline-offset-2"
                          style={{ color: 'var(--mm-accent)' }}
                          data-testid="team-link"
                          // The visible text cannot carry the new-tab warning; the name can.
                          aria-label="View the Metered Mining team. Opens in a new tab."
                        >
                          {teamParts.link}
                        </a>
                        {teamParts.after}
                      </>
                    ) : (
                      MINING_MODE_DESCRIPTIONS[mode]
                    ),
                }))}
              />

            </Step>

            <Step number={4} title="Where rewards go">
              {/* gap-5 rather than gap-7: with the divider's own padding below, the
                  original pair stacked 1.75rem + 1.75rem of air around one hairline. */}
              <div className="mm-panel grid gap-5 p-5 sm:p-6">
                <PayoutAddressField
                  value={payoutAddress}
                  // Not `setPayoutAddress` directly: an edit also marks the field as the
                  // customer's, so a late account-default response cannot overwrite it.
                  onChange={handlePayoutChange}
                  onStateChange={setPayoutState}
                />

                <div style={{ borderTop: '1px solid var(--mm-border)', paddingTop: '1.25rem' }}>
                  <label htmlFor="email" className="mm-label">
                    Email
                  </label>
                  {/*
                    ONE SENTENCE, BOTH STATES, at the owner's request.

                    The signed-in variant used to explain account ownership instead, which
                    answered a question nobody had asked at this point in the form. What a
                    customer wants to know beside an email field is what will be sent to
                    it, and that answer does not change with who is signed in.

                    The ownership fact is not lost: the field is read-only when signed in,
                    and the note underneath says how to order for a different address.
                  */}
                  <p className="mm-help mt-1.5">
                    We&rsquo;ll send order confirmations and updates here.
                  </p>
                  {/*
                    LOCKED WHEN SIGNED IN, because the order belongs to that account either
                    way -- the backend uses the session's email and ignores anything else.
                    An editable field would invite a customer to type an address that
                    silently would not be used, which is exactly the confusion this slice
                    removes. `readOnly` rather than `disabled` so the value is still
                    submitted, selectable and read by assistive technology.
                  */}
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={signedInEmail ?? email}
                    onChange={(event) => {
                      setEmail(event.target.value);
                      // Re-typing clears the message immediately; it comes back on blur
                      // if the address is still wrong. Correcting a field should not
                      // leave an error sitting under it while you fix it.
                      if (emailTouched) setEmailTouched(false);
                    }}
                    onBlur={() => setEmailTouched(true)}
                    readOnly={Boolean(signedInEmail)}
                    aria-readonly={Boolean(signedInEmail)}
                    aria-invalid={showEmailError || undefined}
                    aria-describedby={showEmailError ? 'email-error' : undefined}
                    data-testid="email-input"
                    placeholder="you@example.com"
                    className={`mm-input mt-3${signedInEmail ? ' opacity-70' : ''}`}
                  />
                  {showEmailError && (
                    <p
                      id="email-error"
                      role="alert"
                      className="mm-help mt-2"
                      style={{ color: 'var(--mm-danger)' }}
                      data-testid="email-error"
                    >
                      {emailError}
                    </p>
                  )}
                  {signedInEmail && (
                    <p className="mm-help mt-2" data-testid="email-locked-note">
                      Ordering for a different address? Sign out first.
                    </p>
                  )}
                </div>
              </div>
            </Step>
          </div>

          {/* --- Decision panel ------------------------------------------- */}
          <aside className="grid content-start gap-3 lg:sticky lg:top-8 lg:self-start">
            <OddsDisplay state={oddsState} onRetry={retryOdds} winningValue={winningValue} />

            <DiscountCodeField
              appliedCode={discountCode}
              quoteState={quoteState}
              currency={config.currency}
              disabled={submitting}
              onApply={(code) => {
                setDiscountCode(code);
                setSubmitError(null);
              }}
              onRemove={() => {
                setDiscountCode(null);
                setSubmitError(null);
              }}
            />

            <OrderSummary
              tier={selectedTier}
              hashrateTh={hashrateTh}
              termDays={termDays}
              miningMode={miningMode}
              currency={config.currency}
              payoutState={payoutState}
              oddsState={oddsState}
              quoteState={quoteState}
              appliedCode={discountCode}
            />

            {submitError && (
              <p role="alert" data-testid="submit-error" className="mm-note mm-note--danger">
                <span>{submitError}</span>
              </p>
            )}

            <div>
              <button type="submit" disabled={!canSubmit} data-testid="create-order" className="mm-btn-primary">
                {submitting ? 'Creating order…' : 'Create order'}
              </button>

              <p className="mm-help mt-3 text-center text-xs">
                {blockedReason ?? 'No payment is taken at this step.'}
              </p>
            </div>
          </aside>
        </form>
      </main>

      <SiteFooter />
    </div>
  );
}
